import { Link } from 'react-router-dom';
import { RegistroCta } from '@/features/auth/components/registro-cta';
import {
  TrendingUp,
  Shield,
  Zap,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Minus,
  Target,
  BarChart3,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { DocsLayout } from '../components/docs-layout';

/* ── Planes ── */
const proposedTiers = [
  {
    name: 'Starter',
    price: 99,
    setupFee: 0,
    users: 5,
    target: 'Retail, emprendedores, e-commerce pequeno',
    segment: 'Self-service',
    color: 'from-blue-500 to-cyan-400',
    features: [
      'Hasta 5 usuarios',
      'Cotizaciones ilimitadas',
      '3 modelos de pricing',
      'Asistente IA basico (50 consultas/mes)',
      'Catalogo de productos',
      'PDF con branding basico',
      'Multi-moneda',
      'Soporte por email (48h)',
    ],
    notIncluded: [
      'Workflow de aprobaciones',
      'Pricing dinamico',
      'Integraciones API',
      'Analytics avanzado',
    ],
    justification:
      'Sin setup fee — 100% self-service. El cliente se registra, carga su catalogo y cotiza. El limite de 3 modelos y 50 consultas IA motiva el upgrade a Pro cuando crecen. La barrera de entrada es $0 upfront + $99/mes — mata la objecion "Excel es gratis" con valor inmediato.',
  },
  {
    name: 'Pro',
    price: 199,
    setupFee: 500,
    users: 15,
    target: 'Distribuidoras, importadoras, empresas de TI',
    segment: 'Corporativo',
    color: 'from-violet-500 to-purple-400',
    highlighted: true,
    features: [
      'Hasta 15 usuarios',
      'Cotizaciones ilimitadas',
      'Modelos ilimitados',
      'Asistente IA completo (ilimitado)',
      'Workflow de aprobaciones multinivel',
      'Analytics y reportes avanzados',
      'Integraciones API (ERP, CRM)',
      'Pricing dinamico basico',
      'PDF con branding completo',
      'Soporte prioritario (24h)',
      'Onboarding asistido (incluido en setup)',
    ],
    notIncluded: ['Multi-sucursal', 'IA personalizada', 'SLA garantizado'],
    justification:
      'El plan ancla. Setup fee de $500 cubre onboarding asistido + configuracion inicial (4-6 horas de trabajo). $199/mes con 15 usuarios = $13/usuario — 70% mas barato que HubSpot. El workflow de aprobaciones es el diferenciador que Excel no puede replicar.',
  },
  {
    name: 'Enterprise',
    price: 399,
    setupFee: 1500,
    users: 30,
    extraUserPrice: 9,
    target: 'Corporaciones, multi-sucursal, gobierno',
    segment: 'Enterprise',
    color: 'from-emerald-500 to-green-400',
    features: [
      'Hasta 30 usuarios incluidos',
      'Usuario adicional: $9/mes c/u',
      'Todo lo del plan Pro',
      'Multi-sucursal con permisos por sede',
      'IA personalizada (entrenada con tus datos)',
      'Pricing dinamico avanzado + scraping',
      'Reportes ejecutivos y dashboards',
      'API completa + webhooks',
      'Capacitacion presencial incluida',
      'Soporte prioritario 24/7 + SLA 99.5%',
      'Account manager dedicado',
    ],
    notIncluded: [],
    justification:
      'Setup fee de $1,500 cubre implementacion completa: migracion de datos, integraciones, capacitacion presencial (~20h de trabajo). 30 usuarios incluidos a $399 = $13.3/usuario. A partir del usuario 31, $9/mes c/u genera ingreso incremental con costo marginal casi cero. Una empresa con 50 usuarios paga $399 + 20×$9 = $579/mes — aun 70% mas barato que Salesforce CPQ.',
  },
];

/* ── Professional Services (one-time revenue) ── */

const professionalServices = [
  {
    name: 'Setup + Configuracion',
    price: 500,
    includedIn: 'Pro',
    description: 'Instalacion, catalogo, margenes, usuarios. Incluido en plan Pro y Enterprise.',
  },
  {
    name: 'Capacitacion (remoto)',
    price: 300,
    includedIn: null,
    description: 'Entrenamiento remoto para equipo de ventas (2-3 sesiones).',
  },
  {
    name: 'Capacitacion (presencial)',
    price: 600,
    includedIn: 'Enterprise',
    description: 'Entrenamiento en sitio. Incluido en plan Enterprise.',
  },
  {
    name: 'Migracion de datos',
    price: 200,
    includedIn: null,
    description: 'Importamos catalogos, clientes y datos desde Excel/ERP.',
  },
  {
    name: 'Integracion ERP/CRM',
    price: 800,
    includedIn: null,
    description: 'Conexion con SAP, Oracle, Salesforce u otro sistema.',
  },
  {
    name: 'IA personalizada (setup)',
    price: 500,
    includedIn: 'Enterprise',
    description: 'Entrenamiento de IA con datos historicos del cliente.',
  },
];

/* ── Competitive Landscape ── */

type CompLevel = 'full' | 'partial' | 'none';

interface CompetitorRow {
  name: string;
  origin: string;
  price: string;
  target: string;
  cpq: CompLevel;
  ai: CompLevel;
  governance: CompLevel;
  spanish: CompLevel;
  bolivia: CompLevel;
}

const competitors: CompetitorRow[] = [
  {
    name: 'Prixia',
    origin: 'Bolivia',
    price: '$99-399/org',
    target: 'PYME a Corporativo',
    cpq: 'full',
    ai: 'full',
    governance: 'full',
    spanish: 'full',
    bolivia: 'full',
  },
  {
    name: 'Salesforce CPQ',
    origin: 'USA',
    price: '$75-150/usuario',
    target: 'Enterprise',
    cpq: 'full',
    ai: 'partial',
    governance: 'full',
    spanish: 'full',
    bolivia: 'partial',
  },
  {
    name: 'PandaDoc',
    origin: 'USA',
    price: '$19-49/usuario',
    target: 'PYME',
    cpq: 'partial',
    ai: 'none',
    governance: 'partial',
    spanish: 'full',
    bolivia: 'none',
  },
  {
    name: 'Zoho CRM',
    origin: 'India',
    price: '$14-52/usuario',
    target: 'PYME',
    cpq: 'partial',
    ai: 'partial',
    governance: 'partial',
    spanish: 'full',
    bolivia: 'partial',
  },
  {
    name: 'Alegra',
    origin: 'Colombia',
    price: '$10-50/org',
    target: 'Micro/PYME',
    cpq: 'none',
    ai: 'none',
    governance: 'none',
    spanish: 'full',
    bolivia: 'partial',
  },
  {
    name: 'HubSpot Sales',
    origin: 'USA',
    price: '$45-120/usuario',
    target: 'PYME a Mid',
    cpq: 'partial',
    ai: 'partial',
    governance: 'partial',
    spanish: 'full',
    bolivia: 'none',
  },
  {
    name: 'Excel / Manual',
    origin: '-',
    price: 'Gratis',
    target: 'Todos',
    cpq: 'none',
    ai: 'none',
    governance: 'none',
    spanish: 'full',
    bolivia: 'full',
  },
];

function CompIcon({ level }: { level: CompLevel }) {
  switch (level) {
    case 'full':
      return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    case 'partial':
      return <Minus className="h-4 w-4 text-amber-500" />;
    case 'none':
      return <XCircle className="h-4 w-4 text-red-400" />;
  }
}

/* ── Value Proposition Data ── */

const valueProps = [
  {
    icon: Shield,
    title: 'Gobernanza de Cotizaciones',
    problem: 'Cada vendedor usa su propio Excel. No hay control ni estandarizacion.',
    solution: 'Modelos centralizados, templates estandarizados, reglas de pricing consistentes.',
    value: 'Elimina errores de cotizacion que cuestan entre 5-15% del margen.',
    color: 'from-blue-500 to-cyan-400',
  },
  {
    icon: Zap,
    title: 'Velocidad de Respuesta',
    problem: 'Crear una cotizacion compleja toma 2-4 horas en Excel.',
    solution: 'Cotizaciones complejas en 15-30 minutos con calculos automaticos.',
    value: 'Ahorro de ~125 horas/mes para un equipo de 10 vendedores.',
    color: 'from-violet-500 to-purple-400',
  },
  {
    icon: TrendingUp,
    title: 'Workflow de Aprobacion',
    problem: 'Aprobaciones por email/WhatsApp sin trazabilidad ni historial.',
    solution: 'Flujo automatizado con notificaciones, historial y metricas.',
    value: 'Reduccion del 70% en tiempo de aprobacion. Cero cotizaciones sin autorizar.',
    color: 'from-emerald-500 to-green-400',
  },
  {
    icon: BarChart3,
    title: 'Inteligencia de Pricing con IA',
    problem: 'No hay datos de margenes ni analisis de tendencias. Se descubre despues de vender.',
    solution: 'IA que analiza margenes, sugiere precios y detecta anomalias en tiempo real.',
    value: 'Mejora del margen promedio entre 3-8% con pricing basado en datos.',
    color: 'from-orange-500 to-amber-400',
  },
];


/* ── Page Component ── */

export function PricingStrategyPage() {
  return (
    <DocsLayout
      title="Estrategia de Pricing"
      description="Analisis de mercado y justificacion de los planes de Prixia."
    >
      <div className="space-y-12">
        {/* ── 1. Resumen ejecutivo ── */}
        <section>
          <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-violet-50 dark:from-blue-950/30 dark:to-violet-950/30 border border-blue-100 dark:border-blue-900/50">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
              Resumen Ejecutivo
            </h2>
            <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <p>
                <strong>
                  Prixia es el primer CPQ (Configure, Price, Quote) dedicado para el mercado
                  boliviano y latinoamericano.
                </strong>{' '}
                No existe competencia directa en Bolivia — el competidor real es Excel y WhatsApp.
              </p>
              <p>
                Los planes se diferencian por <strong>dos segmentos</strong>: Retail (precios
                dinamicos, equipos chicos) y Corporativo (cotizaciones complejas, workflow de
                aprobaciones).
              </p>
            </div>
          </div>
        </section>

        {/* ── 2. Propuesta de Valor ── */}
        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Propuesta de Valor
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Cada problema resuelto se traduce en ahorro medible para el cliente.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {valueProps.map((vp, idx) => (
              <Card key={idx} className="overflow-hidden">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`w-10 h-10 rounded-xl bg-gradient-to-br ${vp.color} flex items-center justify-center`}
                    >
                      <vp.icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-base font-bold text-gray-900 dark:text-white">
                      {vp.title}
                    </h3>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="font-semibold text-red-600 dark:text-red-400">
                        Problema:{' '}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">{vp.problem}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-blue-600 dark:text-blue-400">
                        Solucion:{' '}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">{vp.solution}</span>
                    </div>
                    <div className="p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900/50">
                      <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                        Valor:{' '}
                      </span>
                      <span className="text-emerald-600 dark:text-emerald-400">{vp.value}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* ── 3. ROI para el Cliente ── */}
        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            ROI para el Cliente
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Ejemplo: una empresa con 10 vendedores y 50 cotizaciones al mes (datos de ejemplo).
          </p>
          <Card className="overflow-hidden border-2 border-emerald-200 dark:border-emerald-800">
            <div className="h-1.5 bg-gradient-to-r from-emerald-500 to-green-400" />
            <CardContent className="p-6">
              <div className="grid sm:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                    Situacion Actual
                  </h3>
                  <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    <p>10 vendedores haciendo cotizaciones</p>
                    <p>~3 horas por cotizacion en Excel</p>
                    <p>50 cotizaciones/mes</p>
                    <p>
                      <strong className="text-gray-900 dark:text-white">= 150 horas/mes</strong>
                    </p>
                    <p>
                      A $25/hora ={' '}
                      <strong className="text-red-600 dark:text-red-400">$3,750/mes</strong>
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                    Con Prixia
                  </h3>
                  <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    <p>Mismos 10 vendedores</p>
                    <p>~30 min por cotizacion</p>
                    <p>50 cotizaciones/mes</p>
                    <p>
                      <strong className="text-gray-900 dark:text-white">= 25 horas/mes</strong>
                    </p>
                    <p>
                      A $25/hora ={' '}
                      <strong className="text-emerald-600 dark:text-emerald-400">$625/mes</strong>
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                    Resultado
                  </h3>
                  <div className="space-y-3">
                    <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30">
                      <p className="text-xs text-gray-500">Ahorro mensual</p>
                      <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                        $3,125
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30">
                      <p className="text-xs text-gray-500">Costo Prixia (Pro)</p>
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">$199</p>
                    </div>
                    <div className="p-3 rounded-lg bg-violet-50 dark:bg-violet-950/30">
                      <p className="text-xs text-gray-500">ROI</p>
                      <p className="text-2xl font-bold text-violet-600 dark:text-violet-400">
                        15.7x
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  <strong>Nota:</strong> Este calculo solo incluye ahorro de tiempo. No incluye el
                  valor de: eliminar errores de calculo (5-15% del margen), mejor gobernanza,
                  decisiones basadas en datos de IA, y cotizaciones mas rapidas que cierran mas
                  ventas.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* ── 5. Planes + Setup Fees + Servicios ── */}
        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Oferta Comercial Completa
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Pricing por organizacion (no por usuario). Dos fuentes de ingreso: suscripcion mensual +
            servicios profesionales (one-time).
          </p>

          {/* Plans */}
          <div className="space-y-6 mb-8">
            {proposedTiers.map((tier, idx) => (
              <Card
                key={idx}
                className={`overflow-hidden ${tier.highlighted ? 'border-2 border-violet-300 dark:border-violet-700' : ''}`}
              >
                {tier.highlighted && (
                  <div className="h-1.5 bg-gradient-to-r from-violet-500 to-purple-400" />
                )}
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-6">
                    {/* Plan info */}
                    <div className="sm:w-1/4">
                      <div className="flex items-center gap-3 mb-2">
                        <div
                          className={`w-10 h-10 rounded-xl bg-gradient-to-br ${tier.color} flex items-center justify-center`}
                        >
                          <Target className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                            {tier.name}
                          </h3>
                          <p className="text-xs text-gray-500">{tier.segment}</p>
                        </div>
                      </div>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white mt-3">
                        ${tier.price}
                        <span className="text-sm font-normal text-gray-500">/mes</span>
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        Hasta {tier.users} usuarios incluidos
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        ${(tier.price / tier.users).toFixed(1)}/usuario/mes
                      </p>
                      {'extraUserPrice' in tier && (
                        <p className="text-xs font-medium text-violet-600 dark:text-violet-400 mt-1">
                          +${(tier as (typeof proposedTiers)[2]).extraUserPrice}/mes por usuario
                          extra
                        </p>
                      )}
                      {tier.setupFee > 0 ? (
                        <div className="mt-2 inline-flex items-center px-2.5 py-1 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                          <span className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                            Setup: ${tier.setupFee.toLocaleString()} (one-time)
                          </span>
                        </div>
                      ) : (
                        <div className="mt-2 inline-flex items-center px-2.5 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
                          <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                            Sin setup fee
                          </span>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-2">{tier.target}</p>
                    </div>

                    {/* Features */}
                    <div className="sm:w-1/4">
                      <h4 className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-3">
                        Incluye
                      </h4>
                      <ul className="space-y-1">
                        {tier.features.map((f, fIdx) => (
                          <li
                            key={fIdx}
                            className="flex items-start gap-1.5 text-xs text-gray-600 dark:text-gray-400"
                          >
                            <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0 mt-0.5" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Not included */}
                    <div className="sm:w-1/4">
                      {tier.notIncluded.length > 0 && (
                        <>
                          <h4 className="text-xs font-semibold text-red-500 dark:text-red-400 uppercase tracking-wider mb-3">
                            No incluye
                          </h4>
                          <ul className="space-y-1">
                            {tier.notIncluded.map((f, fIdx) => (
                              <li
                                key={fIdx}
                                className="flex items-start gap-1.5 text-xs text-gray-400"
                              >
                                <XCircle className="h-3 w-3 text-red-300 shrink-0 mt-0.5" />
                                {f}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                      {tier.notIncluded.length === 0 && (
                        <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30">
                          <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                            Todo incluido
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Acceso completo a todas las funcionalidades de la plataforma.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Justification */}
                    <div className="sm:w-1/4">
                      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                        Justificacion
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {tier.justification}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Professional Services */}
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
            Servicios Profesionales (one-time)
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Fuente de ingreso adicional por implementacion.
          </p>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-800">
                      <th className="p-3 text-left font-semibold text-gray-900 dark:text-white">
                        Servicio
                      </th>
                      <th className="p-3 text-right font-semibold text-gray-900 dark:text-white">
                        Precio
                      </th>
                      <th className="p-3 text-center font-semibold text-gray-500 text-xs">
                        Incluido en
                      </th>
                      <th className="p-3 text-left font-semibold text-gray-500 text-xs">
                        Descripcion
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {professionalServices.map((svc, idx) => (
                      <tr
                        key={idx}
                        className="border-b border-gray-100 dark:border-gray-800/50 last:border-0"
                      >
                        <td className="p-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                          {svc.name}
                        </td>
                        <td className="p-3 text-right font-bold text-gray-900 dark:text-white">
                          ${svc.price}
                        </td>
                        <td className="p-3 text-center">
                          {svc.includedIn ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-300">
                              {svc.includedIn}+
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">Add-on</span>
                          )}
                        </td>
                        <td className="p-3 text-gray-500 dark:text-gray-400 text-xs">
                          {svc.description}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="mt-4 p-4 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/50">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              <strong>Modelo de ingresos dual:</strong> La suscripcion mensual genera MRR
              predecible. Los servicios profesionales generan ingreso upfront que mejora el flujo de
              caja en los primeros meses y financia el onboarding. Para el P&L, se estiman $700
              promedio por cliente Pro y $2,000 por Enterprise (setup + servicios adicionales).
            </p>
          </div>
        </section>

        {/* ── 6. Comparativa Competitiva ── */}
        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Comparativa Competitiva
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Prixia vs. alternativas disponibles en el mercado boliviano y LATAM.
          </p>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-800">
                      <th className="p-3 text-left font-semibold text-gray-900 dark:text-white">
                        Solucion
                      </th>
                      <th className="p-3 text-left font-semibold text-gray-500 text-xs">Origen</th>
                      <th className="p-3 text-left font-semibold text-gray-500 text-xs">Precio</th>
                      <th className="p-3 text-center font-semibold text-gray-500 text-xs">
                        CPQ Real
                      </th>
                      <th className="p-3 text-center font-semibold text-gray-500 text-xs">IA</th>
                      <th className="p-3 text-center font-semibold text-gray-500 text-xs">
                        Gobernanza
                      </th>
                      <th className="p-3 text-center font-semibold text-gray-500 text-xs">
                        Espanol
                      </th>
                      <th className="p-3 text-center font-semibold text-gray-500 text-xs">
                        Bolivia
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {competitors.map((comp, idx) => (
                      <tr
                        key={idx}
                        className={`border-b border-gray-100 dark:border-gray-800/50 last:border-0 ${
                          idx === 0 ? 'bg-blue-50/50 dark:bg-blue-950/20 font-medium' : ''
                        }`}
                      >
                        <td className="p-3 text-gray-900 dark:text-white whitespace-nowrap">
                          {comp.name}
                        </td>
                        <td className="p-3 text-gray-500">{comp.origin}</td>
                        <td className="p-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {comp.price}
                        </td>
                        <td className="p-3 text-center">
                          <CompIcon level={comp.cpq} />
                        </td>
                        <td className="p-3 text-center">
                          <CompIcon level={comp.ai} />
                        </td>
                        <td className="p-3 text-center">
                          <CompIcon level={comp.governance} />
                        </td>
                        <td className="p-3 text-center">
                          <CompIcon level={comp.spanish} />
                        </td>
                        <td className="p-3 text-center">
                          <CompIcon level={comp.bolivia} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          <div className="mt-3 flex items-center gap-6 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 text-emerald-500" /> Completo
            </span>
            <span className="flex items-center gap-1">
              <Minus className="h-3 w-3 text-amber-500" /> Parcial
            </span>
            <span className="flex items-center gap-1">
              <XCircle className="h-3 w-3 text-red-400" /> No disponible
            </span>
          </div>
        </section>

        {/* ── 9. Ventaja Competitiva ── */}
        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            Ventaja Competitiva
          </h2>
          <div className="p-6 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-900/50 border border-gray-200 dark:border-gray-800">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-3">Por que ganamos</h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-emerald-500">1.</span>
                    <span>
                      <strong>Unico CPQ en Bolivia</strong> — first mover sin competencia directa
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-emerald-500">2.</span>
                    <span>
                      <strong>IA integrada</strong> — ninguna alternativa accesible la ofrece
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-emerald-500">3.</span>
                    <span>
                      <strong>Gobernanza</strong> — resuelve el dolor real que nadie mas atiende
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-emerald-500">4.</span>
                    <span>
                      <strong>90% mas barato</strong> que enterprise CPQ con funcionalidad real
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-emerald-500">5.</span>
                    <span>
                      <strong>Nativo para LATAM</strong> — no es una traduccion, es el producto
                    </span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-3">
                  Riesgos y mitigacion
                </h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-amber-500">1.</span>
                    <span>
                      <strong>Inercia del Excel</strong> — Mitigar con trial gratis + ROI
                      demostrable
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-amber-500">2.</span>
                    <span>
                      <strong>Sensibilidad al precio</strong> — Pricing por org, no por usuario
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-amber-500">3.</span>
                    <span>
                      <strong>Educacion de mercado</strong> — Contenido + casos de exito locales
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-amber-500">4.</span>
                    <span>
                      <strong>Alegra mejora cotizaciones</strong> — Moverse rapido en CPQ profundo
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="text-center py-12 px-6 rounded-2xl bg-gradient-to-r from-blue-50 to-violet-50 dark:from-blue-950/30 dark:to-violet-950/30 border border-blue-100 dark:border-blue-900/50">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Listo para implementar esta estrategia?
          </h2>
          <p className="mt-2 text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
            Los numeros respaldan el modelo. El mercado esta listo. Es hora de ejecutar.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <RegistroCta
              className="inline-flex items-center gap-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-violet-600 px-6 py-3 rounded-lg hover:shadow-lg transition-shadow"
            >
              Ver Planes en Vivo
              <ArrowRight className="w-4 h-4" />
            </RegistroCta>
            <Link
              to="/docs/roi"
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
            >
              Ver Calculadora de ROI
            </Link>
          </div>
        </div>
      </div>
    </DocsLayout>
  );
}
